/** @format */

import React from "react";
import { getImageUrl } from "../../util/getImageUrl";

/**
 * Circular avatar with graceful fallback to initials when no image is provided
 * or the image fails to load.
 *
 * Props:
 *  - src     : image URL (optional)
 *  - name    : full name used to derive initials
 *  - size    : pixel size (default 40)
 *  - rounded : border-radius in px (default 50% of size)
 *  - style   : extra styles to merge
 */
const InitialsAvatar = ({ src, name = "", size = 40, rounded, style = {} }) => {
	const [failed, setFailed] = React.useState(false);

	const initials = React.useMemo(() => {
		const parts = String(name || "")
			.trim()
			.split(/\s+/)
			.filter(Boolean);
		if (parts.length === 0) return "?";
		if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
		return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
	}, [name]);

	// Deterministic pastel background from name
	const bg = React.useMemo(() => {
		let hash = 0;
		for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffff;
		const hue = hash % 360;
		return `hsl(${hue}, 65%, 88%)`;
	}, [name]);
	const fg = React.useMemo(() => {
		let hash = 0;
		for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffff;
		const hue = hash % 360;
		return `hsl(${hue}, 55%, 35%)`;
	}, [name]);

	const common = {
		width: size,
		height: size,
		borderRadius: rounded ?? size / 2,
		objectFit: "cover",
		flexShrink: 0,
		...style,
	};

	if (src && !failed) {
		const resolvedSrc = /^https?:\/\//.test(src) || src.startsWith("data:") || src.startsWith("blob:") ? src : getImageUrl(src) || src;
		return <img src={resolvedSrc} alt={name || "avatar"} style={common} onError={() => setFailed(true)} />;
	}

	return (
		<div
			style={{
				...common,
				backgroundColor: bg,
				color: fg,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontWeight: 600,
				fontSize: Math.max(12, Math.round(size * 0.38)),
				letterSpacing: "0.5px",
				userSelect: "none",
			}}
			aria-label={name}>
			{initials}
		</div>
	);
};

export default InitialsAvatar;
